/**
 * Image Compressor Utility
 * Uses canvas API to compress images
 */

/**
 * Compress image
 * @param {File} file - Original image file
 * @param {Function} onProgress - Progress callback (0-100)
 */
export const compressImage = async (file, onProgress) => {
  try {
    // Create canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Load image
    const img = await loadImage(file);

    // Calculate new dimensions (maintain aspect ratio)
    const maxWidth = 1920;
    const maxHeight = 1080;
    let { width, height } = img;

    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width *= ratio;
      height *= ratio;
    }

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = height;

    // Draw image on canvas
    ctx.drawImage(img, 0, 0, width, height);

    // Convert canvas to blob with compression
    return new Promise((resolve) => {
      // Update progress
      if (onProgress) {
        onProgress(50);
      }

      canvas.toBlob(
        (blob) => {
          if (onProgress) {
            onProgress(100);
          }

          resolve({
            blob: blob,
            name: `compressed_${Date.now()}_${file.name}`,
            size: blob.size,
            originalSize: file.size,
            ratio: (blob.size / file.size).toFixed(2),
          });
        },
        'image/jpeg',
        0.8
      ); // 80% quality
    });
  } catch (err) {
    console.error('[ImageCompressor] Error during compression:', err);
    // Return original file as fallback
    return {
      blob: file,
      name: file.name,
      size: file.size,
      originalSize: file.size,
      ratio: '1.00',
      fallback: true,
    };
  }
};

/**
 * Load image from file
 * @param {File} file - Image file
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};
